import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NativeChessBoard } from './NativeChessBoard';
import { PlayerInfoCard } from './PlayerInfoCard';
import { GameControls } from './GameControls';
import { ArcanePowerPanel } from './ArcanePowerPanel';
import { ArcanePowerOrb } from './ArcanePowerOrb';
import { GameStatusBar } from './GameStatusBar';
import { ArcaneBlitzTutorial } from './ArcaneBlitzTutorial';
import { useLocalClock } from '@/hooks/useLocalClock';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAudioFeedback } from '@/hooks/useAudioFeedback';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useArcaneBlitzStats } from '@/hooks/useArcaneBlitzStats';
import { useArcaneSeason } from '@/hooks/useArcaneSeason';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Position } from '@/lib/chess-engine';
import { getGameStatus } from '@/lib/chess-engine';
import { getEngineMove } from '@/lib/unified-engine';
import {
  ArcaneBlitzGameState,
  SuperpowerType,
  SUPERPOWERS,
  ORB_DEFS,
  createInitialArcaneState,
  validateArcaneMove,
  activateSuperpower,
  canActivateSuperpower,
  getMana,
  isFrozen,
  isShielded,
  getOrbAt,
  OrbType,
} from '@/lib/arcane-blitz-engine';
import { toast } from 'sonner';
import { Sparkles, Zap, ArrowLeft } from 'lucide-react';

interface ArcaneBlitzBoardProps {
  opponent: {
    name: string;
    isBot: boolean;
    elo?: number;
  };
  onBackToMenu?: () => void;
}

// Build visual overlay: frozen (blue), shielded (gold), orbs (colored dots)
function useHighlightMap(state: ArcaneBlitzGameState) {
  const frozen: Position[] = state.frozenPieces.map(f => f.position);
  const shielded: Position[] = state.shieldedPieces.map(s => s.position);
  // Fortify highlight
  if (state.fortifiedColor && state.fortifiedTurnsRemaining > 0) {
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        const p = state.board[r][c];
        if (p?.color === state.fortifiedColor) shielded.push([r, c]);
      }
    }
  }
  return { frozen, shielded };
}

export function ArcaneBlitzBoard({ opponent, onBackToMenu }: ArcaneBlitzBoardProps) {
  const [gameState, setGameState] = useState<ArcaneBlitzGameState>(createInitialArcaneState());
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [gameFinished, setGameFinished] = useState(false);
  const botThinkingRef = useRef(false);
  const [botThinking, setBotThinking] = useState(false); // UI display only
  const [showTutorial, setShowTutorial] = useState(false);
  const [resultRecorded, setResultRecorded] = useState(false);
  const [superpowersUsedThisGame, setSuperpowersUsedThisGame] = useState(0);
  const [peakManaThisGame, setPeakManaThisGame] = useState(3);

  const { profile } = useUserProfile();
  const { playMoveSound, playGameEventSound, playSuperpowerSound } = useAudioFeedback();
  const { triggerHaptic } = useHapticFeedback();
  const { stats, markTutorialComplete, recordGameResult } = useArcaneBlitzStats();
  const { recordSeasonWin } = useArcaneSeason();

  // Show tutorial on first visit
  useEffect(() => {
    if (stats && !stats.tutorial_completed) {
      setShowTutorial(true);
    }
  }, [stats]);

  // Track peak mana
  useEffect(() => {
    const currentMana = getMana(gameState, playerColor);
    setPeakManaThisGame(prev => Math.max(prev, currentMana));
  }, [gameState]);

  const { whiteTimeMs, blackTimeMs } = useLocalClock(
    '10+0',
    gameState.sideToMove,
    gameFinished ? 'ended' : 'ongoing'
  );

  const playerColor: 'white' | 'black' = 'white';
  const isPlayerTurn = gameState.sideToMove === playerColor && !gameFinished;

  // ── Bot AI ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (gameState.sideToMove === 'black' && opponent.isBot && !gameFinished && !botThinkingRef.current) {
      botThinkingRef.current = true;
      setBotThinking(true); // UI indicator only

      const makeMove = async () => {
        try {
          const botColor: 'white' | 'black' = 'black';
          const moveNum = gameState.fullmoveNumber;
          let currentState = gameState;

          // Occasionally fire cheapest superpower (free action, doesn't end turn)
          if (moveNum > 2 && moveNum % 4 === 0) {
            const cheapPower = SUPERPOWERS.filter(p => canActivateSuperpower(currentState, p.type, botColor))
              .sort((a, b) => a.cost - b.cost)[0];
            if (cheapPower) {
              let result = { success: false } as ReturnType<typeof activateSuperpower>;
              if (!cheapPower.needsTarget) {
                result = activateSuperpower(currentState, cheapPower.type, botColor);
              } else {
                const offensive = ['nova-strike', 'hex-freeze'].includes(cheapPower.type);
                for (let r = 0; r < 8 && !result.success; r++) {
                  for (let c = 0; c < 8 && !result.success; c++) {
                    const p = currentState.board[r][c];
                    if (p) {
                      if ((offensive && p.color === 'white') || (!offensive && p.color === 'black')) {
                        result = activateSuperpower(currentState, cheapPower.type, botColor, [r, c]);
                      }
                    }
                  }
                }
              }
              if (result.success && result.newState) {
                currentState = result.newState;
                setGameState(currentState);
                playGameEventSound('promotion');
                toast.info(`🧙 ${opponent.name} used ${cheapPower.emoji} ${cheapPower.name}!`);
                // Brief pause so player sees the superpower before the normal move
                await new Promise(resolve => setTimeout(resolve, 400));
              }
            }
          }

          // Normal move (always runs, using post-superpower state)
          const engineResult = await getEngineMove(currentState, opponent.elo || 1200);
          if (engineResult.move?.from && engineResult.move?.to) {
            const moveResult = validateArcaneMove(currentState, engineResult.move.from, engineResult.move.to);
            if (moveResult.isLegal && moveResult.newState) {
              setGameState(moveResult.newState);
              playMoveSound();
              if (moveResult.orbCollected) {
                toast.info(`${opponent.name} collected a power orb!`);
              }
            }
          }
        } catch (err) {
          console.error('[ArcaneBlitz] Bot error', err);
        } finally {
          botThinkingRef.current = false;
          setBotThinking(false);
        }
      };

      const delay = setTimeout(makeMove, 600);
      return () => clearTimeout(delay);
    }
  }, [gameState.sideToMove, gameState.fullmoveNumber, opponent.isBot, gameFinished]);

  // Check game over
  const gameStatus = getGameStatus(gameState);
  useEffect(() => {
    if ((gameStatus.status === 'checkmate' || gameStatus.status === 'stalemate') && !gameFinished) {
      setGameFinished(true);
      if (!resultRecorded) {
        setResultRecorded(true);
        const won = gameStatus.status === 'checkmate' && gameStatus.winner === playerColor;
        recordGameResult(won, superpowersUsedThisGame, peakManaThisGame);
        if (won) recordSeasonWin();
      }
    }
  }, [gameStatus.status]);

  // ── Superpower activation ─────────────────────────────────────────────────
  const handleActivatePower = useCallback((type: SuperpowerType) => {
    if (!isPlayerTurn) return;
    const def = SUPERPOWERS.find(p => p.type === type)!;

    if (!canActivateSuperpower(gameState, type, playerColor)) {
      toast.error(`Not enough mana! ${def.name} costs ${def.cost} ✨`);
      return;
    }

    if (!def.needsTarget) {
      // Instant power (Time Warp)
      const result = activateSuperpower(gameState, type, playerColor);
      if (result.success && result.newState) {
        setGameState(result.newState);
        setSuperpowersUsedThisGame(prev => prev + 1);
        toast.success(`${def.emoji} ${def.name} activated!`);
        playSuperpowerSound(type);
        triggerHaptic('success');
      } else {
        toast.error(result.error || 'Power failed.');
      }
      return;
    }

    // Enter targeting mode
    if (gameState.chargingPower?.type === type) {
      // Toggle off
      setGameState(prev => ({ ...prev, chargingPower: null }));
      setSelectedSquare(null);
    } else {
      setGameState(prev => ({ ...prev, chargingPower: { type } }));
      setSelectedSquare(null);
      toast.info(`${def.emoji} ${def.name}: click a target on the board`);
    }
  }, [gameState, isPlayerTurn, playerColor, playGameEventSound, triggerHaptic]);

  // ── Square click handler ──────────────────────────────────────────────────
  const handleSquareClick = useCallback((position: Position) => {
    if (!isPlayerTurn) return;
    triggerHaptic('selection');

    const charging = gameState.chargingPower;

    // ── Superpower targeting mode ───────────────────────────────────────────
    if (charging) {
      const def = SUPERPOWERS.find(p => p.type === charging.type)!;

      if (def.needsDoubleTarget) {
        if (!charging.firstTarget) {
          // First click: remember target
          setGameState(prev => ({
            ...prev,
            chargingPower: { ...charging, firstTarget: position },
          }));
          toast.info(`${def.emoji} Now click the second target`);
          return;
        } else {
          // Second click: execute
          const result = activateSuperpower(gameState, charging.type, playerColor, charging.firstTarget, position);
          if (result.success && result.newState) {
          setGameState({ ...result.newState, chargingPower: null });
          toast.success(`${def.emoji} ${def.name}!`);
          playGameEventSound('promotion');
          triggerHaptic('success');
          } else {
            toast.error(result.error || 'Power failed.');
            setGameState(prev => ({ ...prev, chargingPower: null }));
          }
          setSelectedSquare(null);
          return;
        }
      } else {
        // Single-target power
        const result = activateSuperpower(gameState, charging.type, playerColor, position);
        if (result.success && result.newState) {
          setGameState({ ...result.newState, chargingPower: null });
          toast.success(`${def.emoji} ${def.name}!`);
          playGameEventSound('promotion');
          triggerHaptic('success');
        } else {
          toast.error(result.error || 'Invalid target.');
          setGameState(prev => ({ ...prev, chargingPower: null }));
        }
        setSelectedSquare(null);
        return;
      }
    }

    // ── Normal move mode ────────────────────────────────────────────────────
    const [row, col] = position;
    const piece = gameState.board[row][col];

    if (selectedSquare) {
      const [sr, sc] = selectedSquare;
      if (sr === row && sc === col) {
        setSelectedSquare(null);
        return;
      }

      const moveResult = validateArcaneMove(gameState, selectedSquare, position);
      if (moveResult.isLegal && moveResult.newState) {
        setGameState(moveResult.newState);
        setSelectedSquare(null);
        playMoveSound();
        if (moveResult.orbCollected) {
          const orbDef = ORB_DEFS.find(d => d.type === moveResult.orbCollected);
          toast.success(`${orbDef?.emoji} ${orbDef?.name}! ${orbDef?.description}`);
          playGameEventSound('promotion');
        }
        return;
      }
      setSelectedSquare(null);
    }

    if (piece && piece.color === playerColor) {
      setSelectedSquare(position);
    }
  }, [gameState, selectedSquare, isPlayerTurn, playerColor, playMoveSound, playGameEventSound, triggerHaptic]);

  // ── Highlights ───────────────────────────────────────────────────────────
  const { frozen: frozenSquares, shielded: shieldedSquares } = useHighlightMap(gameState);
  const allHighlighted: Position[] = [...frozenSquares, ...shieldedSquares];

  const mana = getMana(gameState, playerColor);

  // Tutorial overlay
  if (showTutorial) {
    return (
      <ArcaneBlitzTutorial
        onComplete={() => { setShowTutorial(false); markTutorialComplete(); }}
        onSkip={() => setShowTutorial(false)}
      />
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-3 bg-gradient-to-br from-violet-950 via-purple-900/30 to-indigo-950 min-h-screen relative">
      {/* Ambient particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-8 left-12 text-teen-cyan text-lg animate-float-particle opacity-60">✨</div>
        <div className="absolute top-24 right-16 text-teen-pink text-xl animate-float-particle opacity-50" style={{ animationDelay: '1.2s' }}>⭐</div>
        <div className="absolute bottom-24 left-1/3 text-teen-gold text-lg animate-float-particle opacity-50" style={{ animationDelay: '2.5s' }}>💫</div>
        <div className="absolute bottom-16 right-1/4 text-teen-purple text-xl animate-float-particle opacity-40" style={{ animationDelay: '3.8s' }}>✦</div>
      </div>

      {/* Header bar */}
      <div className="flex items-center justify-between mb-4 relative z-10">
        <Button variant="outline" size="sm" onClick={onBackToMenu}
          className="border-teen-purple/30 text-foreground hover:bg-teen-purple/10">
          <ArrowLeft className="w-4 h-4 mr-1" /> Menu
        </Button>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-teen-cyan animate-sparkle" />
          <span className="font-bold text-lg bg-gradient-to-r from-teen-cyan via-teen-pink to-teen-purple bg-clip-text text-transparent">
            Arcane Blitz
          </span>
          <Badge variant="outline" className="border-teen-cyan/40 text-teen-cyan text-xs">
            {mana}✨ mana
          </Badge>
        </div>
        <div className="w-20" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_280px] gap-4 relative z-10">
        {/* Left panel */}
        <div className="space-y-3">
          <PlayerInfoCard
            name={opponent.name}
            isBot={opponent.isBot}
            elo={opponent.elo}
            timeMs={blackTimeMs}
            isActivePlayer={gameState.sideToMove === 'black'}
            color="black"
          />

          {/* Orb legend */}
          {gameState.powerOrbs.length > 0 && (
            <Card className="border-teen-cyan/20 bg-violet-950/60">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">⚡ Power Orbs on board</p>
                <div className="space-y-1">
                  {gameState.powerOrbs.map((orb, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <ArcanePowerOrb type={orb.type} size="sm" />
                      <span className="text-foreground/70">
                        {String.fromCharCode(97 + orb.position[1])}{8 - orb.position[0]}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bot thinking */}
          {botThinking && (
            <Card className="border-teen-purple/30 bg-violet-950/50">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-teen-purple animate-pulse">🧙 {opponent.name} is casting…</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Center: board */}
        <div className="space-y-3">
          {/* Status */}
          <div className={`
            text-center py-2 px-3 rounded-lg border backdrop-blur-sm
            ${gameState.chargingPower
              ? 'bg-teen-purple/20 border-teen-purple/50 animate-pulse'
              : 'bg-violet-950/50 border-teen-purple/20'}
          `}>
            <span className="text-sm font-semibold text-foreground">
              {gameState.chargingPower
                ? `${SUPERPOWERS.find(p => p.type === gameState.chargingPower?.type)?.emoji} Targeting — click a square`
                : gameStatus.status === 'ongoing' || gameStatus.status === 'check'
                  ? isPlayerTurn ? '⚡ Your turn' : `🧙 ${opponent.name}'s turn`
                  : gameStatus.status === 'checkmate'
                  ? `♚ Checkmate! ${gameStatus.winner} wins`
                  : '🤝 Stalemate'}
            </span>
          </div>

          {/* Board with orb overlays */}
          <div className="relative">
            <NativeChessBoard
              gameState={gameState}
              onSquareClick={handleSquareClick}
              highlightedSquares={allHighlighted}
              selectedSquare={selectedSquare}
              flipped={false}
              enableSwipeGestures
              enableHapticFeedback
            />

            {/* Orb overlays — positioned absolutely over board squares */}
            {gameState.powerOrbs.map((orb, i) => {
              const [r, c] = orb.position;
              // Board is 8x8; each cell is 12.5% width/height
              return (
                <div
                  key={i}
                  className="absolute pointer-events-none z-20 flex items-center justify-center"
                  style={{
                    left: `calc(${c} * 12.5% + 12.5% / 2 - 10px)`,
                    top: `calc(${r} * 12.5% + 12.5% / 2 - 10px)`,
                    width: 20,
                    height: 20,
                  }}
                >
                  <ArcanePowerOrb type={orb.type} />
                </div>
              );
            })}

            {/* Frozen piece overlays */}
            {gameState.frozenPieces.map((fp, i) => {
              const [r, c] = fp.position;
              return (
                <div
                  key={`freeze-${i}`}
                  className="absolute pointer-events-none z-20 flex items-center justify-center text-sm"
                  style={{
                    left: `calc(${c} * 12.5%)`,
                    top: `calc(${r} * 12.5%)`,
                    width: '12.5%',
                    height: '12.5%',
                  }}
                >
                  ❄️
                </div>
              );
            })}
          </div>

          <GameControls
            onResign={() => { setGameFinished(true); onBackToMenu?.(); }}
            canTakeback={false}
          />
        </div>

        {/* Right panel */}
        <div className="space-y-3">
          <PlayerInfoCard
            name={profile?.display_name || 'You'}
            isBot={false}
            timeMs={whiteTimeMs}
            isActivePlayer={isPlayerTurn}
            color="white"
          />

          <ArcanePowerPanel
            state={gameState}
            playerColor={playerColor}
            onActivate={handleActivatePower}
            isPlayerTurn={isPlayerTurn}
          />
        </div>
      </div>
    </div>
  );
}
